// ----------------------------------------------------------------------------
// Product Form Web Component: iamQuantityInput <iam-quantity-input>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Quantity selector input for the product form.
// - Allows the user to increment or decrement the quantity value
// - Allows the user to manually enter a quantity value
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:quantity:changed' → Listens for when the quantity is changed,
//                                    but does nothing by default
// - 'productForm:variant:changed'  → PDP selector rebuilds options and clamps
//                                    qty to the resolved max
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Updates the quantity value when the up or down buttons are interacted with
//   or the user enters a value manually
// ----------------------------------------------------------------------------
// Public Methods:
// - `setValue(quantity: number)` → Set the quantity to a specific value
// - `getValue()`                 → Get the current quantity value
// ----------------------------------------------------------------------------
// Gotchas:
// - If an external component wants to modify the quantity, leverage the `setValue`
//   method to ensure the appropriate change events are triggered.
// - Instead of directly modifying the quantity, you likely want to register
//   form validation rules to prevent invalid quantities (that the user can
//   see feedback regarding and resolve manually)
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import registerCustomElement from 'scripts/helpers/register-custom-element';

// Validation Extensions (in priority order)
import './quantity-input--variant-exists'; // Validate that the variant actually exists
import './quantity-input--inventory-limits'; // Validate qty against available inventory
import './quantity-input--available'; // Validate that the variant is available for sale
// import './quantity-input--metafield-limits'; // Validate qty against product/variant metafield limits
// import './quantity-input--b2b-limits';       // Validate qty against Shopify B2B limits
import './quantity-input--hardcoded-limits'; // Validate qty against hardcoded limits

// Style includes
import './_quantity-input.scss';

// -------------------------------------
// Functionality
// -------------------------------------

class iamQuantity extends iamFormElement {
  /**
   * Connected Callback
   */
  connectedCallback() {
    this.variantControllerSelector = this.dataset.variantController;
    this.variantController = this.closest(this.variantControllerSelector);
    this.errorContainer = this.variantController.querySelector('.form__errors-container');
    super.connectedCallback();
  }

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Validation
    if (!this.validateRequiredAttributes(['variantControllerSelector', 'variantController', 'errorContainer'])) return;

    // Event listeners for buttons
    this.querySelectorAll('.quantity-input__button').forEach(button => {
      button.addEventListener('click', this.onButtonClick.bind(this));
      button.addEventListener('keypress', this.onButtonPress.bind(this));
    });
    if (this.hasAttribute('data-pdp-quantity-selector')) {
      var _this$control;
      this.variantLimits = this.parseVariantLimits();
      this.settingMax = this.parseSettingMax();
      (_this$control = this.control) === null || _this$control === void 0 || _this$control.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });
      iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
        context: this.iamForm
      });
      this.reconcileQuantity();
    }
  }

  /**
   * Handles the button click event and updates the quantity.
   *
   * @param {Event} event - The click event.
   */
  onButtonClick(event) {
    this.onStepArrow(event);
  }

  /**
   * Handles the button press event and updates the quantity if 'Enter' is pressed.
   *
   * @param {KeyboardEvent} event - The key press event.
   */
  onButtonPress(event) {
    if (event.key === 'Enter') {
      this.onStepArrow(event);
    }
  }

  /**
   * Updates the quantity value and triggers the change event.
   * It applies filters to allow modification of the quantity value before setting it.
   *
   * @param {Event} event - The event triggering the quantity update.
   */
  onStepArrow(event) {
    // Prevent modification if the input is disabled
    if (this.control.disabled) return;

    // Update the quantity based on the button clicked
    if (event.target.classList.contains('quantity-input__button--down')) {
      this.control.stepDown();
    } else {
      this.control.stepUp(); // Default to increment
    }
    this.triggerChange();
  }

  /**
   * Set the value to a specific quantity.
   *
   * @param {number} quantity - The quantity to set
   */
  setValue(quantity) {
    this.control.value = quantity;
    this.triggerChange();
  }

  /**
   * Get the current quantity value
   */
  getValue() {
    return parseInt(this.control.value, 10);
  }

  /**
   * Trigger change event if the input was manipulated programmatically.
   */
  triggerChange() {
    this.control.dispatchEvent(new Event('input', {
      bubbles: true,
      // Allow the event to bubble up the DOM
      cancelable: true,
      // Allow the event to be canceled
      composed: true // Allow event to traverse shadow DOM
    }));
  }

  /**
   * Get the associated item data
   */
  getItemData() {
    var _this$variantControll;
    return ((_this$variantControll = this.variantController) === null || _this$variantControll === void 0 ? void 0 : _this$variantControll.getCurrentData()) || {};
  }

  /**
   * Rebuild option list and clamp qty when the selected variant changes.
   */
  onVariantChange() {
    this.reconcileQuantity();
  }
  parseSettingMax() {
    var raw = this.dataset.maxQuantity;
    var parsed = parseInt(raw, 10);
    return parsed > 0 ? parsed : null;
  }
  parseVariantLimits() {
    var script = this.querySelector('script[type="application/json"][data-quantity-variant-limits]');
    if (!script) return {};
    try {
      var list = JSON.parse(script.textContent);
      return (Array.isArray(list) ? list : []).reduce((map, entry) => {
        if (entry !== null && entry !== void 0 && entry.id) {
          map[String(entry.id)] = entry;
        }
        return map;
      }, {});
    } catch (_unused) {
      return {};
    }
  }
  getResolvedMax() {
    var _itemData$variant, _this$variantLimits, _itemData$product, _ref;
    var itemData = this.getItemData();
    var variantId = (_itemData$variant = itemData.variant) === null || _itemData$variant === void 0 ? void 0 : _itemData$variant.id;
    var limits = ((_this$variantLimits = this.variantLimits) === null || _this$variantLimits === void 0 ? void 0 : _this$variantLimits[String(variantId)]) || {};
    var tags = (_itemData$product = itemData.product) === null || _itemData$product === void 0 ? void 0 : _itemData$product.tags;
    if (tags !== null && tags !== void 0 && tags.includes('Free Trial')) return 1;
    var inventoryMax = null;
    var tracked = Boolean(limits.inventory_management);
    var deny = limits.inventory_policy === 'deny';
    var quantity = Number(limits.inventory_quantity);
    if (tracked && deny && quantity > 0) {
      inventoryMax = quantity;
    }
    var settingMax = this.settingMax;
    if (inventoryMax != null && settingMax != null) {
      return Math.min(inventoryMax, settingMax);
    }
    return (_ref = inventoryMax !== null && inventoryMax !== void 0 ? inventoryMax : settingMax) !== null && _ref !== void 0 ? _ref : null;
  }
  reconcileQuantity() {
    if (!this.control) return;
    var min = 1;
    var optionListCap = 99;
    var resolvedMax = this.getResolvedMax();
    var optionMax = Math.max(min, Math.min(resolvedMax !== null && resolvedMax !== void 0 ? resolvedMax : optionListCap, optionListCap));
    var current = this.getValue();
    if (Number.isNaN(current) || current < min) {
      current = min;
    }
    var next = Math.min(current, optionMax);
    this.rebuildOptions(optionMax, next);
    if (next !== current) {
      this.triggerChange();
      this.control.dispatchEvent(new Event('change', {
        bubbles: true,
        cancelable: true,
        composed: true
      }));
    }
  }
  rebuildOptions(optionMax, selectedValue) {
    var select = this.control;
    if (!select || select.tagName !== 'SELECT') return;
    var fragment = document.createDocumentFragment();
    for (var i = 1; i <= optionMax; i += 1) {
      var option = document.createElement('option');
      option.value = String(i);
      option.textContent = String(i);
      fragment.appendChild(option);
    }
    select.replaceChildren(fragment);
    select.value = String(selectedValue);
  }
}

// Define the custom element
registerCustomElement('iam-quantity-input', iamQuantity);